import { useEffect, useRef } from "react";
import "./alert-container.scss";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { clearAlert } from "src/redux/alert";
import useBlur from "src/hooks/useBlur";
import Button from "src/components/Button";
import { useTranslation } from "react-i18next";

export type AlertProps = {
	type?: string;
	title: string;
	text?: any;
	stayOpen?: boolean;
	button?: string;
	onConfirm?: () => void;
};

const variants = {
	container: {
		enter: {
			opacity: 1,
			transition: {
				when: "beforeChildren",
				staggerChildren: 0.5,
			},
		},
		exit: { opacity: 0 },
	},
	alert: {
		enter: {
			y: 0,
			opacity: 1,
			scale: 1,
		},
		exit: {
			y: 200,
			scale: 0.2,
			opacity: 0,
		},
	},
};

const AlertContainer = () => {
	const dispatch = useDispatch();
	const alert = useSelector((state: any) => state.alert);

	useEffect(() => {
		if (alert.title !== "" && !alert.stayOpen && alert.type !== "confirm") {
			setTimeout(() => {
				dispatch(clearAlert());
			}, 2000);
		}
	}, [alert, dispatch]);

	return (
		<AnimatePresence>
			{alert.title && (
				<motion.div
					initial="exit"
					animate="enter"
					exit="exit"
					variants={variants.container}
					className="alert-container"
				>
					<Alert {...{ ...alert }} />
				</motion.div>
			)}
		</AnimatePresence>
	);
};

const Alert = ({ title, type, text, button, onConfirm }: AlertProps) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const ref = useRef(null);
	const close = () => dispatch(clearAlert());
	useBlur(ref, close);
	const classes = [
		"px-8 p-8 flex flex-col gap-4 rounded-lg bg-white mr-8 ml-8 max-w-[460px]",
	];

	if (type) {
		classes.push(`alert-${type}`);
	}

	return (
		<motion.div
			ref={ref}
			variants={variants.alert}
			className={classes.join(" ")}
		>
			<div className="flex flex-col gap-2">
				<h2 className="text-center text-[24px]">{title}</h2>
				{text && <p className="text-center px-4 mb-0">{text}</p>}
			</div>
			{type === "confirm" ? (
				<div className="flex-col-reverse md:flex-row flex gap-4">
					<Button
						className="flex-1 md:flex-auto"
						type="gray"
						onClick={close}
					>
						{t("cancel", { ns: "buttons" })}
					</Button>
					<Button
						className="flex-1 md:flex-auto"
						type="primary"
						onClick={() => {
							close();
							if (onConfirm) {
								onConfirm();
							}
						}}
					>
						{button || t("confirm", { ns: "buttons" })}
					</Button>
				</div>
			) : (
				button && <Button onClick={close}>{button}</Button>
			)}
		</motion.div>
	);
};

export default AlertContainer;
